export const SUPPORTED_LANGUAGES = ["es", "en", "pt", "fr", "de", "it", "zh", "ja", "ru"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  es: "Español",
  en: "English",
  pt: "Português",
  fr: "Français",
  de: "Deutsch",
  it: "Italiano",
  zh: "中文",
  ja: "日本語",
  ru: "Русский",
};

/** Países (código ISO-3166-1 alfa-2, el mismo formato que envía la cabecera
 *  `cf-ipcountry` de Cloudflare) mapeados al idioma soportado más adecuado.
 *  Cualquier país no listado cae al valor por defecto ("en"). */
const COUNTRY_LANGUAGE: Record<string, SupportedLanguage> = {
  // Español
  MX: "es", ES: "es", AR: "es", CO: "es", CL: "es", PE: "es", VE: "es", EC: "es",
  GT: "es", CU: "es", BO: "es", DO: "es", HN: "es", PY: "es", SV: "es", NI: "es",
  CR: "es", PA: "es", UY: "es", GQ: "es",
  // Portugués
  PT: "pt", BR: "pt", AO: "pt", MZ: "pt", CV: "pt", GW: "pt", ST: "pt", TL: "pt",
  // Francés
  FR: "fr", BE: "fr", SN: "fr", CI: "fr", ML: "fr", TN: "fr", BF: "fr", NE: "fr",
  TG: "fr", CD: "fr", CG: "fr", GA: "fr", CM: "fr", LU: "fr", MC: "fr",
  // Alemán
  DE: "de", AT: "de", LI: "de",
  // Italiano
  IT: "it", SM: "it", VA: "it",
  // Chino
  CN: "zh", TW: "zh", HK: "zh", MO: "zh",
  // Japonés
  JP: "ja",
  // Ruso
  RU: "ru", BY: "ru", KZ: "ru", KG: "ru", TJ: "ru",
};

const TIMEZONE_LANGUAGE: Record<string, SupportedLanguage> = {
  "America/Mexico_City": "es",
  "America/Cancun": "es",
  "America/Merida": "es",
  "America/Monterrey": "es",
  "America/Matamoros": "es",
  "America/Chihuahua": "es",
  "America/Ciudad_Juarez": "es",
  "America/Ojinaga": "es",
  "America/Mazatlan": "es",
  "America/Bahia_Banderas": "es",
  "America/Hermosillo": "es",
  "America/Tijuana": "es",
  "Europe/Madrid": "es",
  "Europe/Lisbon": "pt",
  "America/Sao_Paulo": "pt",
  "Europe/Paris": "fr",
  "Europe/Berlin": "de",
  "Europe/Rome": "it",
  "Asia/Shanghai": "zh",
  "Asia/Hong_Kong": "zh",
  "Asia/Taipei": "zh",
  "Asia/Tokyo": "ja",
  "Europe/Moscow": "ru",
};

export function detectLanguageFromCountry(country: string | null | undefined): SupportedLanguage | null {
  if (!country) return null;
  return COUNTRY_LANGUAGE[country.toUpperCase()] ?? null;
}

export function detectLanguageFromTimeZone(timeZone?: string | null): SupportedLanguage | null {
  if (!timeZone) return null;
  return TIMEZONE_LANGUAGE[timeZone] ?? null;
}

export function resolveSupportedLanguage(candidates: readonly string[]): SupportedLanguage | null {
  for (const raw of candidates) {
    const short = raw?.trim().slice(0, 2).toLowerCase();
    if ((SUPPORTED_LANGUAGES as readonly string[]).includes(short)) {
      return short as SupportedLanguage;
    }
  }
  return null;
}

/** Usa las preferencias del navegador/SO, respetando su orden, y las reduce
 *  al idioma soportado más cercano. Funciona en web, Android y Electron. */
export function detectLanguageFromNavigator(): SupportedLanguage | null {
  const candidates = typeof navigator !== "undefined" && navigator.languages?.length
    ? navigator.languages
    : [typeof navigator !== "undefined" ? navigator.language : ""];
  return resolveSupportedLanguage(candidates);
}
